import useApi from 'common/hooks/useApi';
import useThunkDispatch from 'common/hooks/useThunkDispatch';
import useTypedSelector from 'common/hooks/useTypedSelector';
import ActionStatus from 'common/types/ActionStatus';
import FetchStatus from 'common/types/FetchStatus';
import FormVerificationResponse from 'domain/components/verify-identity/FormVerificationResponse';
import ProfileVerificationStatusCd from 'domain/enums/ProfileVerificationStatusCd';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { ApplicationStep } from '../../domain/enums/ApplicationStep';
import ApplyForRegistrationPresenter from './ApplyForRegistrationPresenter';
import {
  clearAllDrafts,
  createDraft,
  getDraft,
  resetDraft,
  submitDraftApplication,
} from './DraftApplicationSlice';
import getMaxAvailableStep from './get-max-available-step';

function ApplyForRegistration() {
  const [isLoading, setIsLoading] = useState(false);
  const [existingPrompt, setExistingPrompt] = useState(false);
  const [defaultStep, setDefaultStep] = useState(ApplicationStep.contact);

  // Load draft application on load
  const [
    { data: formVerificationData, loading: formVerificationLoading },
    fetchVerification,
  ] = useApi<FormVerificationResponse>({
    url: `/profile/formVerification`,
    method: 'GET',
  });

  // const [, fetchPostDraftCount] = useApi<number>({
  //   url: '/applications/countPostDraft',
  //   method: 'GET',
  // });

  useEffect(() => {
    fetchVerification();
  }, [fetchVerification]);

  const dispatch = useThunkDispatch();
  const identityStatus = formVerificationData?.status;

  const promptExisting = useCallback(() => {
    setExistingPrompt(true);
  }, []);

  const { meta, data, profileAddress, operatorsLicense } = useTypedSelector(
    (state) => ({
      data: state.draftApplication.data,
      meta: state.draftApplication.meta,
      profileAddress: state.profile.data.address,
      operatorsLicense: state.profile.data.licenseNumber,
    })
  );

  const maxStep = getMaxAvailableStep(data);

  const chooseExisting = useCallback(() => {
    setExistingPrompt(false);
    if (maxStep === ApplicationStep.review) {
      setReviewing(true);
    } else {
      setDefaultStep(maxStep);
    }
  }, [maxStep]);

  // On load, get existing registration if identity is uploaded
  useEffect(() => {
    async function onLoad() {
      try {
        setIsLoading(true);
        const app = await dispatch(getDraft());
        // const appCountPostDraft = (await fetchPostDraftCount()).data;
        // const hasShuol = Boolean(operatorsLicense); // TODO check if user has a shuol

        if (app) {
          if (getMaxAvailableStep(app) > ApplicationStep.contact) {
            // existing draft application found
            // prompt user to use existing or create new
            // short circuit to avoid shuol prompt
            return promptExisting();
          }
        } else {
          // no drafts found,
          // create new application
          dispatch(createDraft());
        }
      } finally {
        setIsLoading(false);
      }
    }
    if (
      identityStatus &&
      identityStatus !== ProfileVerificationStatusCd.INITIAL
    ) {
      onLoad();
    }
  }, [dispatch, identityStatus, operatorsLicense]);

  const [reviewing, setReviewing] = useState(false);

  const { push: pushHistory } = useHistory();
  const [errors, setErrors] = useState<string[]>([]);
  const [submitStatus, setSubmitStatus] = useState(ActionStatus.none);

  const resetApplication = () => {
    setDefaultStep(ApplicationStep.contact); // TODO test that this works
    dispatch(resetDraft());
    setErrors([]);
    setSubmitStatus(ActionStatus.none);
    setExistingPrompt(false);
    setReviewing(false);
  };

  const submit = async () => {
    setSubmitStatus(ActionStatus.saving);
    try {
      const res = await dispatch(submitDraftApplication());
      if (res.ok) {
        ReactGA.event({
          category: 'Application',
          action: 'Submitted',
          label: data.groupRefKey?.toString(),
        });
        setSubmitStatus(ActionStatus.saved);
        return;
      }
      setErrors(
        res.errors || ['Unable to submit application. Please try again later.']
      );
    } catch (e) {
      setErrors(['Unable to submit application. Please try again later.']);
    }
    setSubmitStatus(ActionStatus.failed);
  };

  const clearAndReturnDashboard = (withMessage: boolean) => {
    dispatch(clearAllDrafts());
    pushHistory(
      '/dashboard',
      withMessage
        ? {
            message: 'Application has been cleared.',
            type: 'success',
          }
        : undefined
    );
  };

  return (
    <ApplyForRegistrationPresenter
      isPromptingExistingApp={existingPrompt}
      onExistingClick={chooseExisting}
      onResetClick={resetApplication}
      identity={{
        rejected:
          formVerificationData?.status !== null &&
          formVerificationData?.status === ProfileVerificationStatusCd.REJECTED,
        uploaded:
          formVerificationData?.status !== null &&
          formVerificationData?.status !== ProfileVerificationStatusCd.INITIAL,
        loading: formVerificationLoading,
      }}
      loading={isLoading || meta.fetchStatus !== FetchStatus.loaded}
      data={data}
      errors={errors}
      onSubmit={submit}
      profileAddress={profileAddress}
      onClearApplication={() => clearAndReturnDashboard(true)}
      onReturnToDashboard={() => clearAndReturnDashboard(false)}
      submitStatus={submitStatus}
      isReviewing={reviewing}
      onReviewClick={() => setReviewing(true)}
      onBackToApplicationClick={() => {
        setReviewing(false);
        setDefaultStep(ApplicationStep.details);
      }}
      defaultStep={defaultStep}
    />
  );
}

export default ApplyForRegistration;
